<template>
  <div>
    <h2>
      {{ $t(userHasPassword ? 'ChangePassword' : 'SetPassword') }}
    </h2>

    <form @submit.prevent class="pt-5">
      <div
        v-if="userHasPassword"
        class="mb-5">
        <password-input
          v-model="currentPassword"
          class="w-full"
          :label="$t('$General.CurrentPassword')"
          :name="$t('$General.CurrentPassword')"
          v-validate="'required|password'"
          data-vv-validate-on="blur|input"
          :danger="currentPasswordHasErrors"
          :danger-text="currentPasswordErrorMsg"
          val-icon-danger="clear"
          required
          @input="currentPasswordError = ''"/>

        <forgot-password-link @click="activeForgotPasswordModal = true"/>
      </div>

      <div class="mb-3">
        <password-input
          ref="newPassword"
          v-model="newPassword"
          class="w-full"
          :label="$t('$General.NewPassword')"
          :name="$t('$General.NewPassword')"
          v-validate="'required|password'"
          data-vv-validate-on="blur|input"
          :danger="errors.has($t('$General.NewPassword'))"
          :danger-text="errors.first($t('$General.NewPassword'))"
          val-icon-danger="clear"
          required/>
      </div>

      <div class="mb-3">
        <password-input
          v-model="confirmNewPassword"
          class="w-full"
          :label="$t('$General.ConfirmNewPassword')"
          :name="$t('$General.ConfirmNewPassword')"
          v-validate="'required|password|confirmed:newPassword'"
          :data-vv-as="$t('$General.NewPassword')"
          data-vv-validate-on="blur|input"
          :danger="errors.has($t('$General.ConfirmNewPassword'))"
          :danger-text="errors.first($t('$General.ConfirmNewPassword'))"
          val-icon-danger="clear"
          required/>
      </div>

      <div class="flex items-center justify-start">
        <vs-button
          :disabled="!updateProfilePasswordIsValid"
          @click="updateProfilePassword()">
          {{ $t('$General.Update') }}
        </vs-button>

        <vs-button
          v-show="updateProfilePasswordIsChanged"
          class="ml-3 vs-button-cancel-flat"
          color="danger"
          @click="resetPasswordModel()">
          {{ $t('$General.Cancel') }}
        </vs-button>
      </div>
    </form>

    <send-forgot-password-email-popup
      v-model="activeForgotPasswordModal"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

// Components
import SendForgotPasswordEmailPopup from '@/views/pages/auth/components/SendForgotPasswordEmailPopup.vue';
import ForgotPasswordLink from '@/views/modules/_components/ForgotPasswordLink.vue';
import PasswordInput from '@/components/PasswordInput.vue';

/**
 * Component to allow the user view and update its profile
 *
 * @module views/modules/user-account/UserAccountProfileGeneral
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'UserAccountProfilePassword',
  i18n: {
    messages: {
      en: {
        ChangePassword: 'Change password',
        SetPassword: 'Set password',
        ChangePasswordNotificationTitle: 'Change password',
        ChangePasswordNotificationMsg: 'Password changed successfully',
        ForgotPasswordMsg: 'Forgot password?',
      },
    },
  },
  components: {
    SendForgotPasswordEmailPopup,
    ForgotPasswordLink,
    PasswordInput,
  },
  data() {
    return {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',

      currentPasswordError: '',
      activeForgotPasswordModal: false,
    };
  },
  computed: {
    ...mapGetters({
      getUserFlag: 'auth/getUserFlag',
    }),
    currentPasswordHasErrors() {
      return this.errors.has(this.$t('$General.CurrentPassword'))
        || !!this.currentPasswordError;
    },
    currentPasswordErrorMsg() {
      return this.errors.first(this.$t('$General.CurrentPassword')) || this.currentPasswordError;
    },
    userHasPassword() {
      return this.getUserFlag('hasPassword');
    },
    updateProfilePasswordIsValid() {
      return !this.errors.any()
        && ((this.currentPassword !== ''
          && !this.currentPasswordError)
          || !this.userHasPassword)
        && this.newPassword !== ''
        && this.confirmNewPassword !== '';
    },
    updateProfilePasswordIsChanged() {
      return this.currentPassword !== ''
        || this.newPassword !== ''
        || this.confirmNewPassword !== '';
    },
  },
  methods: {
    ...mapActions({
      updatePassword: 'auth/updatePassword',
    }),
    resetPasswordModel() {
      this.currentPassword = '';
      this.newPassword = '';
      this.confirmNewPassword = '';

      this.$validator.reset();
    },
    async updateProfilePassword() {
      if (this.errors.any()) {
        return;
      }

      this.$vs.loading({ type: 'radius' });

      const payload = {
        currentPassword: this.currentPassword,
        newPassword: this.newPassword,
        confirmNewPassword: this.confirmNewPassword,
      };

      if (!this.userHasPassword) {
        delete payload.currentPassword;
      }

      try {
        await this.updatePassword(payload);
        this.resetPasswordModel();

        // Emit global event to clear forgot password rate limiting
        window.dispatchEvent(new CustomEvent('passwordResetSuccess'));
        
        // Clear localStorage directly since components may be on different pages
        localStorage.removeItem('forgotPasswordLastSent');

        this.$showSuccessActionNotification({
          title: this.$t('ChangePasswordNotificationTitle'),
          text: this.$t('ChangePasswordNotificationMsg'),
        });
      } catch (err) {
        if (err.response && err.response.data && err.response.data.currentPassword) {
          this.currentPasswordError = err.response.data.message;
        } else {
          throw err;
        }
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>
